import billingApi from '@/lib/billingApi'
import type { ProjectZomboidVersionCatalog } from '@/lib/projectZomboidVersions'

export type ServiceEstimate = {
  active: boolean
  specs: {
    cpu: number
    memory: number
    storage: number
    avgTransfer: number
    runtimeHours: number
  }
  monthly_usd: number
  currency: string
}

export type ServiceItem = {
  product?: string
  nest_id: number
  nest_name: string
  egg_id?: number
  egg_uuid?: string
  name: string
  description?: string
  docker_image?: string
  features?: string[]
  slug: string
  logo_key: string
  background_key?: string
  icon_url?: string
  background_url?: string
  /** Optional CSS object-position supplied with the authoritative artwork. */
  background_position?: string
  screenshots?: string[]
  rawg_id?: number
  rawg_name?: string
  rawg_description?: string
  estimate?: ServiceEstimate
  package_pricing?: {
    status: 'published' | 'unpublished' | 'unavailable'
    from_monthly_minor?: number
    currency?: string
    package_count?: number
  }
  /** Hoard package-admission capability for this exact catalog egg. */
  package_admission_supported?: boolean
  badges?: string[]
  action_label?: string
  children?: ServiceItem[]
  hidden_from_marketplace?: boolean
  minecraft?: MinecraftTemplateMetadata
  search_context?: string
  wizard_preselect?: Record<string, unknown>
  project_zomboid_versions?: ProjectZomboidVersionCatalog
}

export type MinecraftDeploymentType = 'server' | 'proxy' | 'network_component'
export type MinecraftEdition = 'java' | 'bedrock'
export type MinecraftExperience = 'vanilla' | 'plugins' | 'mods' | 'hybrid'

export type MinecraftTemplateMetadata = {
  egg_id: number
  product: 'minecraft'
  deployment_type: MinecraftDeploymentType
  edition: MinecraftEdition | null
  experience: MinecraftExperience | null
  software: string
  display_name: string
  display_description?: string | null
  recommended: boolean
  advanced: boolean
  maintenance_status: 'active' | 'legacy' | 'eol'
  new_deployments_enabled: boolean
  requires_client_mods: boolean
  supports_plugins: boolean
  supports_mods: boolean
  supports_crossplay: boolean
  supported_versions: string[]
  search_aliases: string[]
  feature_bindings: Record<string, unknown>
  upstream_type?: string | null
  icon_url?: string | null
}

export type MinecraftWorkflow = {
  product: 'minecraft'
  templates: MinecraftTemplateMetadata[]
  defaults: { plugin_software: string; proxy_software: string }
  feature_labels: Record<string, string>
}

export type MinecraftCompatibility = {
  software: string
  versions: string[]
  source: string
  stale: boolean
}

export type MinecraftDeploymentStatus = {
  uuid: string
  deployment_kind: 'server' | 'network'
  status: 'queued' | 'validating' | 'provisioning' | 'configuring' | 'completed' | 'failed' | 'rolled_back' | 'cancelled'
  intent?: { name?: string }
  progress?: { step?: string; completed?: number; total?: number } | null
  result?: { kind: 'server' | 'network'; server_uuid?: string; proxy_server_uuid?: string; network_uuid?: string } | null
  error_code?: string | null
  error_message?: string | null
  current_stage: string
  completed_steps: number
  total_steps: number
  components: MinecraftDeploymentComponent[]
  connections: MinecraftDeploymentConnection[]
  event_log: Array<{ id: number; event_type: string; component_id: string | null; status: string; message: string; created_at: string }>
}

const activeMinecraftDeploymentStatuses = new Set<MinecraftDeploymentStatus['status']>([
  'queued',
  'validating',
  'provisioning',
  'configuring',
])

export function isActiveMinecraftDeployment(deployment: MinecraftDeploymentStatus): boolean {
  return activeMinecraftDeploymentStatuses.has(deployment.status)
}

export type MinecraftDeploymentComponentStatus = 'waiting' | 'creating' | 'installing' | 'configuring' | 'starting' | 'checking' | 'online' | 'passed' | 'failed' | 'removing' | 'cancelled' | 'skipped'
export type MinecraftDeploymentComponent = { id: string; name: string; type: 'public' | 'proxy' | 'backend' | 'lobby' | 'limbo' | 'health'; role?: string | null; status: MinecraftDeploymentComponentStatus; message: string }
export type MinecraftDeploymentConnection = { id: string; source: string; target: string; status: 'waiting' | 'configuring' | 'connected' | 'failed' | 'cancelled' | 'skipped'; message: string }

export type EligibleMinecraftBackend = {
  uuid: string
  name: string
  node_id: number
  location_id: number
  software: string
  edition: MinecraftEdition
  experience: MinecraftExperience
  version?: string | null
}

export type MinecraftModpack = {
  id: string
  slug: string
  name: string
  description: string
  icon_url?: string | null
  loaders: string[]
  game_versions: string[]
}

export async function listServices(): Promise<ServiceItem[]> {
  const key = 'servicesCacheV2'
  const now = Date.now()
  try {
    const raw = sessionStorage.getItem(key)
    if (raw) {
      const parsed = JSON.parse(raw) as { ts: number; data: ServiceItem[] }
      // 30-second TTL — keep marketplace fresh after admin media updates
      if (parsed && Array.isArray(parsed.data) && now - parsed.ts < 30_000) {
        return parsed.data
      }
    }
  } catch { /* session storage is optional */ }

  const res = await billingApi.get<{ data: ServiceItem[] }>('/subscription/services')
  const data = Array.isArray(res.data?.data) ? res.data.data : []
  try { sessionStorage.setItem(key, JSON.stringify({ ts: now, data })) } catch { /* session storage is optional */ }
  return data
}

export function matchMinecraftSearch(service: ServiceItem, query: string): ServiceItem | null {
  const q = query.trim().toLowerCase()
  if (!q || service.product !== 'minecraft') return service.product === 'minecraft' ? service : null
  if ('bedrock'.includes(q) || q.includes('bedrock')) {
    return { ...service, search_context: 'Bedrock Edition Minecraft server', wizard_preselect: { kind: 'server', edition: 'bedrock' } }
  }
  const match = (service.children || []).find(child => {
    const meta = child.minecraft
    return `${child.name} ${meta?.display_name || ''} ${meta?.software || ''} ${(meta?.search_aliases || []).join(' ')}`.toLowerCase().includes(q)
  })
  if (!match?.minecraft) return null
  const meta = match.minecraft
  const isNetwork = meta.deployment_type !== 'server'
  return {
    ...service,
    search_context: isNetwork ? `${meta.display_name} — Minecraft network` : meta.experience === 'mods' ? `${meta.display_name} — Modded Minecraft server` : meta.experience === 'hybrid' ? `${meta.display_name} — Hybrid mod and plugin Minecraft server` : `${meta.display_name} — Minecraft server software`,
    wizard_preselect: isNetwork
      ? { kind: 'network', proxySoftware: meta.deployment_type === 'proxy' ? meta.software : 'velocity' }
      : { kind: 'server', edition: meta.edition, experience: meta.experience, software: meta.software },
  }
}

export async function getMinecraftWorkflow(): Promise<MinecraftWorkflow> {
  const res = await billingApi.get<{ data: MinecraftWorkflow }>('/subscription/minecraft/workflow')
  return res.data.data
}

export async function getMinecraftCompatibility(software: string): Promise<MinecraftCompatibility> {
  const res = await billingApi.get<{ data: MinecraftCompatibility }>('/subscription/minecraft/compatibility', { params: { software } })
  return res.data.data
}

export async function createMinecraftDeployment(intent: Record<string, unknown>, idempotencyKey: string): Promise<MinecraftDeploymentStatus> {
  const res = await billingApi.post<{ data: MinecraftDeploymentStatus }>('/subscription/minecraft/deployments', intent, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return res.data.data
}

export async function getMinecraftDeployment(uuid: string): Promise<MinecraftDeploymentStatus> {
  const res = await billingApi.get<{ data: MinecraftDeploymentStatus }>(`/subscription/minecraft/deployments/${uuid}`)
  return res.data.data
}

export async function listMinecraftDeployments(kind?: 'server' | 'network', active = false): Promise<MinecraftDeploymentStatus[]> {
  const res = await billingApi.get<{ data: MinecraftDeploymentStatus[] }>('/subscription/minecraft/deployments', { params: { kind, active: active ? 1 : undefined } })
  return res.data.data
}

export async function retryMinecraftDeployment(uuid: string): Promise<MinecraftDeploymentStatus> {
  const res = await billingApi.post<{ data: MinecraftDeploymentStatus }>(`/subscription/minecraft/deployments/${uuid}/retry`)
  return res.data.data
}

export async function cancelMinecraftDeployment(uuid: string): Promise<MinecraftDeploymentStatus> {
  const res = await billingApi.post<{ data: MinecraftDeploymentStatus }>(`/subscription/minecraft/deployments/${uuid}/cancel`)
  return res.data.data
}

export async function getEligibleMinecraftBackends(): Promise<EligibleMinecraftBackend[]> {
  const res = await billingApi.get<{ data: EligibleMinecraftBackend[] }>('/subscription/minecraft/eligible-backends')
  return res.data.data
}

export async function searchMinecraftModpacks(query: string): Promise<MinecraftModpack[]> {
  const res = await billingApi.get<{ data: MinecraftModpack[] }>('/subscription/minecraft/modpacks', { params: { q: query } })
  return res.data.data
}

export default { listServices, getMinecraftWorkflow, getMinecraftCompatibility, createMinecraftDeployment, getMinecraftDeployment, listMinecraftDeployments, retryMinecraftDeployment, cancelMinecraftDeployment, getEligibleMinecraftBackends, searchMinecraftModpacks }
